import React from 'react';
import Icone from '../primitivos/Icone';
import { ContextoFaixaDaMarca } from '../primitivos/FaixaDaMarca';

/**
 * Cabeçalho de tela (at-55, frente A). Evolui o `components/header/Header.tsx`,
 * que serve 23 telas e continua existindo para elas durante a convivência.
 *
 * Duas formas, decisão Q2 (Victor, 08/09/2026):
 *   • mobile  — faixa LARANJA: gatilho do menu + voltar + sobretítulo + título
 *   • desktop — faixa BRANCA: voltar + divisor + sobretítulo + título + ações
 *
 * ⚠️ `gatilhoMenu` é INJETADO pelo `AppShell` (via `cloneElement`) e não deve ser
 * passado à mão: quem controla o estado de aberto/fechado do drawer é o shell.
 *
 * ⚠️ As ações ficam agrupadas num flex, e isso é de propósito. O padrão legado
 * (`.right-button`) é `position:absolute; right:10px`, então dois botões caíam na
 * MESMA coordenada, um invisível atrás do outro (bug real da at-24). Aqui quem é
 * posicionado é o grupo; os botões ficam no fluxo.
 */

interface PropsPageHeader {
  titulo: string;
  /** Linha curta acima do título (ex.: "Nova Adesão"). Opcional. */
  sobretitulo?: string;
  /**
   * Texto discreto **ao lado** do título, precedido de travessão (ex.: o primeiro
   * nome do consultor: "Adesões — Carlos").
   *
   * ⚠️ Só aparece no desktop, como no protótipo: no mobile a faixa já disputa
   * espaço entre gatilho do menu, voltar, título, ação da tela e sino.
   */
  complemento?: string;
  /**
   * Ação de voltar. ⚠️ Quem decide PARA ONDE voltar é o domínio — ver
   * `useVoltar()` em `components/shell/ShellSaac.tsx`, que preserva a regra de
   * 01/09/2026: volta pelo histórico e só cai num endereço fixo quando não há
   * tela anterior dentro do app.
   */
  aoVoltar?: () => void;
  acaoDireita?: React.ReactNode;
  /** Injetado pelo AppShell. */
  gatilhoMenu?: React.ReactNode;
  /**
   * Ações globais (o sino de novidades), injetadas pelo AppShell.
   *
   * ⚠️ Vão **depois** de `acaoDireita`, na extremidade: são as mesmas em toda
   * tela, e posição estável é o que faz o usuário achá-las. A ação da tela fica
   * à esquerda delas.
   */
  acoesGlobais?: React.ReactNode;
}

const PageHeader: React.FC<PropsPageHeader> = ({
  titulo,
  sobretitulo,
  complemento,
  aoVoltar,
  acaoDireita,
  gatilhoMenu,
  acoesGlobais,
}) => (
  <>
    {/* ───── MOBILE (faixa laranja) ─────
        `print:tw-hidden` (at-64): cabeçalho de navegação não pertence a um
        documento impresso. A prévia do laudo de vistoria é a única tela que
        imprime hoje, e a regra do shell vale para qualquer outra que venha. */}
    <header className="print:tw-hidden tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 lg:tw-hidden">
      {gatilhoMenu}

      {aoVoltar && (
        <button
          type="button"
          onClick={aoVoltar}
          aria-label="Voltar"
          className="tw-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white/15 tw-text-white hover:tw-bg-white/25 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-white/60"
        >
          <Icone nome="voltar" tamanho={18} traco={2.5} />
        </button>
      )}

      <div className="tw-min-w-0 tw-flex-1">
        {sobretitulo && (
          <p className="tw-truncate tw-text-xs tw-font-semibold tw-text-white/70">{sobretitulo}</p>
        )}
        <h1 className="tw-truncate tw-font-marca tw-text-lg tw-font-bold tw-leading-tight tw-text-white">
          {titulo}
        </h1>
      </div>

      {(acaoDireita || acoesGlobais) && (
        /*
         * 🔴 **A ação é a MESMA das duas faixas, e os fundos são opostos** — laranja aqui, branco no
         * desktop. O Provider avisa ao `Botao` que ele está sobre a marca, e ele troca de pele
         * sozinho (branco translúcido, como o hambúrguer e o voltar ao lado).
         *
         * Sem isso, um `Botao secundario` — branco sólido com borda e sombra, feito para painel
         * claro — vira um bloco gritante no meio do laranja. Foi o que Victor viu em 10/09/2026:
         * *"um negócio branco no meio do topbar laranja, completamente destoado"*. Ver
         * `primitivos/FaixaDaMarca.ts`.
         *
         * ⚠️ Vale para TODA tela com `acaoDireita`, não só a que motivou a correção: `/adesoes`
         * ("Nova Adesão +", que era laranja sobre laranja), `/carteira`, `/vistoria`.
         */
        <ContextoFaixaDaMarca.Provider value={true}>
          <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
            {acaoDireita}
            {acoesGlobais}
          </div>
        </ContextoFaixaDaMarca.Provider>
      )}
    </header>

    {/* ───── DESKTOP (faixa branca) ───── */}
    {/*
      🔴 **`print:!tw-hidden` com important, e SÓ aqui** (at-64).

      Este é o único elemento do cabeçalho cujo `display` é disputado por duas
      variantes de media: `lg:tw-flex` (que o mostra) e `print:tw-hidden` (que o
      esconderia). Elas têm a **mesma especificidade**, então vence quem vier por
      último no CSS **gerado** — e o Tailwind emite `@media print` **antes** dos
      breakpoints, o que faria `lg:tw-flex` ganhar numa folha larga o suficiente
      para casar com `lg`. É a armadilha que o `Card.tsx` documenta.

      O `!` resolve sem depender de ordem. No mobile não é preciso: lá o
      `lg:tw-hidden` já esconde, e `print:tw-hidden` não compete com nada.
    */}
    <header className="print:!tw-hidden tw-hidden tw-items-center tw-justify-between tw-border-b tw-border-superficie-borda tw-bg-white tw-px-8 tw-py-4 tw-shadow-sm lg:tw-flex">
      <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-4">
        {/* ⚠️ O gatilho aparece TAMBÉM no desktop desde 08/09/2026: a navegação
            deixou de ser uma sidebar fixa e virou um painel que sobrepõe (ver
            `AppShell`). Sem ele aqui, o desktop ficaria sem acesso ao menu. */}
        {gatilhoMenu}

        {aoVoltar && (
          <>
            <button
              type="button"
              onClick={aoVoltar}
              className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-2 tw-py-1 tw-text-sm tw-font-semibold tw-text-texto-suave tw-cursor-pointer hover:tw-text-brand focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand/40"
            >
              <Icone nome="voltar" tamanho={16} />
              Voltar
            </button>
            <span className="tw-h-5 tw-w-px tw-bg-superficie-borda" aria-hidden="true" />
          </>
        )}

        <div className="tw-min-w-0">
          {sobretitulo && (
            <p className="tw-truncate tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-texto-desabilitado">
              {sobretitulo}
            </p>
          )}
          <div className="tw-flex tw-min-w-0 tw-items-baseline tw-gap-2">
            <h1 className="tw-truncate tw-font-marca tw-text-xl tw-font-bold tw-text-texto-titulo">
              {titulo}
            </h1>
            {complemento && (
              <span className="tw-truncate tw-text-sm tw-text-texto-desabilitado">
                — {complemento}
              </span>
            )}
          </div>
        </div>
      </div>

      {(acaoDireita || acoesGlobais) && (
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-3">
          {acaoDireita}
          {acoesGlobais}
        </div>
      )}
    </header>
  </>
);

export default PageHeader;
